import { createSlice } from '@reduxjs/toolkit';
import { USER_PROFILE_TAB } from 'views/V2/UserProfile/constants';

const initialState = {
    resultData: {},
    workflowOverview: {},
    userMetaData: {},
    adminJwtToken: {},
    riskScoreAndInsight: {},
    workflows: [],
    hasAcknowledgedCharges: {
        gwc: false,
        soc: false,
    },
    mvrAttemptDetails: [],
    generatedUserPdfReports: {},
    timelineStatuses: {},
    extendedChecksStatus: {},
    medallionSessionInfoType: null,
    activeTab: USER_PROFILE_TAB.OVERVIEW,
    hasRiskScoreReRun: false,
    activeModalType: null,

    // Loading states for different API groups
    isGeneralApiLoading: false,
    isTwoFaAndRiskApiLoading: false,
    isCriminalChecksApiLoading: false,
    isBackgroundChecksApiLoading: false,
};

export const userProfileSlice = createSlice({
    name: 'userProfile',
    initialState,
    reducers: {
        setResultData: (state, action) => {
            state.resultData = action.payload;
        },
        setWorkflowOverview: (state, action) => {
            state.workflowOverview = action.payload;
        },
        setUserMetaData: (state, action) => {
            state.userMetaData = action.payload;
        },
        setAdminJwtTokenSettings: (state, action) => {
            state.adminJwtToken = action.payload;
        },
        setRiskScoreAndInsightResult: (state, action) => {
            state.riskScoreAndInsight = action.payload;
        },
        setWorkflows: (state, action) => {
            state.workflows = action.payload;
        },
        setHasAcknowledgedCharges: (state, action) => {
            state.hasAcknowledgedCharges = {
                ...state.hasAcknowledgedCharges,
                [action.payload.type]: action.payload.value,
            };
        },
        setMvrAttemptDetails: (state, action) => {
            state.mvrAttemptDetails = action.payload;
        },

        setTimelineItemStatus: (state, action) => {
            const { type, status } = action.payload;
            state.timelineStatuses[type] = status;
        },
        setExtendedChecksStatus: (state, action) => {
            state.extendedChecksStatus = action.payload;
        },
        updateGeneratedPdfReport: (state, action) => {
            const { reportDetails, userAccessCode } = action.payload;
            state.generatedUserPdfReports[userAccessCode] = reportDetails;
        },
        removeReportFromGeneratedPdfReport: (state, action) => {
            const { userAccessCode } = action.payload;
            delete state.generatedUserPdfReports?.[userAccessCode];
        },
        // Loading state actions
        setGeneralApiLoading: (state, action) => {
            state.isGeneralApiLoading = action.payload;
        },
        setTwoFaAndRiskApiLoading: (state, action) => {
            state.isTwoFaAndRiskApiLoading = action.payload;
        },
        setCriminalChecksApiLoading: (state, action) => {
            state.isCriminalChecksApiLoading = action.payload;
        },
        setBackgroundChecksApiLoading: (state, action) => {
            state.isBackgroundChecksApiLoading = action.payload;
        },
        setMedallionSessionInfoType: (state, action) => {
            state.medallionSessionInfoType = action.payload;
        },
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },
        setHasRiskScoreReRun: (state, action) => {
            state.hasRiskScoreReRun = action.payload;
        },
        setActiveModalType: (state, action) => {
            state.activeModalType = action.payload;
        },
        clearActiveModalType: (state) => {
            state.activeModalType = null;
        },
    },
});

export const {
    setResultData,
    setWorkflowOverview,
    setUserMetaData,
    setAdminJwtTokenSettings,
    setRiskScoreAndInsightResult,
    setWorkflows,
    setHasAcknowledgedCharges,
    setMvrAttemptDetails,
    setTimelineItemStatus,
    setExtendedChecksStatus,
    updateGeneratedPdfReport,
    removeReportFromGeneratedPdfReport,
    setGeneralApiLoading,
    setTwoFaAndRiskApiLoading,
    setCriminalChecksApiLoading,
    setBackgroundChecksApiLoading,
    setMedallionSessionInfoType,
    setActiveTab,
    setHasRiskScoreReRun,
    setActiveModalType,
    clearActiveModalType,
} = userProfileSlice.actions;

export default userProfileSlice.reducer;
